import { SearchState } from '@searchkit/client';
import { isEqual } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { Preference } from 'sr-types/lib/search/v1/graphql';
import {
    reorderPinnedTabs,
    serializePinnedSavedFiltersPreferenceValue,
    serializeSavedFilterPreferenceValue,
    upsertPinnedTab
} from '../../production/helpers/savedFilterPreferenceUtils';
import {
    buildUpdatedPreferencesAfterSave,
    sortPinnedSavedFilters,
    transformSavedFilterPreferences
} from '../../production/helpers/savedFiltersDataUtils';
import {
    applySearchState,
    buildFilterKey,
    clearSelectedSavedFilter,
    composeBaseFilters,
    findMatchingSavedFilter,
    getDisplayFilters,
    normalizeFilters,
    processFilterData,
    readSelectedSavedFilter,
    writeSelectedSavedFilter
} from '../../production/helpers/savedFiltersUtils';
import { FILTER_PREFERENCE_KEY, PINNED_TABS_PREFERENCE_KEY } from '../../production/productionConstants';
import { useSavePreferences } from '../auth/hooks/savePreferences';
import { UserContext } from '../auth/UserContext';
import { FORMAT_API_ACTION } from '../constants/reportConstants';
import {
    SAVED_FILTERS_CONSTANTS,
    type Filter,
    type PinnedSavedFiltersPreferenceValue,
    type SavedFilterItem
} from '../types/savedFilters';
import type { SavePreferenceFormData } from '../types/savePreference';
import { failedToastMessage, successToastMessage } from '../utils/commonUtils';
import { usePersonalPinnedTabs } from './usePersonalPinnedTabs';

type UseSavedFiltersOptions = {
    requiredFilters?: string[];
    immutableFilters?: Filter[];
    excludedFilterIdentifiers?: string[];
};

type SearchkitApi = {
    searchState: SearchState;
    setSearchState: (state: SearchState) => void;
    search: () => void;
};

export type NewTabDisabledReason = 'noFilter' | 'alreadyPinned';

export type NewTabSaveModalConfig = {
    mode: 'create' | 'edit';
    initialData: SavePreferenceFormData;
    preferenceId?: string;
    accountId?: string;
};

function seedPinnedTabsFromSavedFilters(savedFilters: SavedFilterItem[]): PinnedSavedFiltersPreferenceValue {
    return {
        schemaVersion: 1,
        pinnedTabs: savedFilters.flatMap((filter) =>
            filter.pinAsTab && filter.id
                ? [
                      {
                          preferenceId: filter.id,
                          pinnedAt: filter.pinnedAt || new Date(0).toISOString()
                      }
                  ]
                : []
        )
    };
}

function buildOptimisticPinnedTabsPreferences(input: {
    preferences: Preference[];
    existingPinnedPreference?: Preference;
    nextPinnedValue: PinnedSavedFiltersPreferenceValue;
    accountId: string;
    applicationName: string;
    pinnedTabsPreferenceKey: string;
}): Preference[] {
    const {
        preferences,
        existingPinnedPreference,
        nextPinnedValue,
        accountId,
        applicationName,
        pinnedTabsPreferenceKey
    } = input;
    const serializedValue = serializePinnedSavedFiltersPreferenceValue(nextPinnedValue);
    const optimisticPinnedRow = existingPinnedPreference
        ? {
              ...existingPinnedPreference,
              value: serializedValue
          }
        : {
              id: '',
              accountId,
              application: applicationName,
              key: pinnedTabsPreferenceKey,
              value: serializedValue,
              name: SAVED_FILTERS_CONSTANTS.PINNED_TABS_PREFERENCE_NAME,
              shared: false,
              view: '',
              agent: '',
              headline: '',
              uri: ''
          };

    return [
        ...preferences.filter(
            (preference) =>
                !(
                    preference.key === pinnedTabsPreferenceKey &&
                    preference.accountId === accountId &&
                    preference.application === applicationName
                )
        ),
        optimisticPinnedRow
    ];
}

export const useSavedFilters = (
    preferenceEntityKey: string,
    api: SearchkitApi,
    options: UseSavedFiltersOptions = {}
) => {
    const {
        preferencesLoaded,
        activeOrganizationAccount,
        organizationAccountDetails,
        deletePreference,
        getSharedPreferences,
        preferences,
        refreshPreferences,
        userProfile,
        application
    } = useContext(UserContext);
    const { savePreferences } = useSavePreferences();

    const storageKey = `${SAVED_FILTERS_CONSTANTS.SELECTED_KEY_PREFIX}_${application.name}_${preferenceEntityKey}`;
    const preferenceKey = `${FILTER_PREFERENCE_KEY}${preferenceEntityKey}`;
    const pinnedTabsPreferenceKey = `${PINNED_TABS_PREFERENCE_KEY}${preferenceEntityKey}`;

    const [saveFilterOpen, setSaveFilterOpen] = useState(false);
    const [appliedSavedFilterId, setAppliedSavedFilterId] = useState<string | null>(null);
    const [savingFilter, setSavingFilter] = useState(false);
    const [deletingFilter, setDeletingFilter] = useState(false);
    const [newTabSaveModalOpen, setNewTabSaveModalOpen] = useState(false);
    const hasRestoredRef = useRef(false);
    const persistingPinnedTabsRef = useRef(false);

    const { personalPinnedTabs, persistPersonalPinnedTabs } = usePersonalPinnedTabs({
        preferencesLoaded,
        preferences,
        pinnedTabsPreferenceKey,
        accountId: userProfile?.accountId,
        applicationName: application.name,
        savePreferences
    });

    const savedFilters = useMemo<Array<SavedFilterItem>>(() => {
        if (!preferencesLoaded) return [];
        const entries = getSharedPreferences(null, null, preferenceKey) || [];
        return transformSavedFilterPreferences(entries, personalPinnedTabs?.value ?? null);
    }, [preferencesLoaded, getSharedPreferences, preferenceKey, personalPinnedTabs]);

    const pinnedSavedFilters = useMemo(
        () => sortPinnedSavedFilters(savedFilters, personalPinnedTabs?.value ?? null),
        [savedFilters, personalPinnedTabs]
    );

    const currentFilters = useMemo(() => api?.searchState?.filters || [], [api?.searchState?.filters]);
    const excludedFilterIdentifiers = useMemo(
        () => options.excludedFilterIdentifiers || [],
        [options.excludedFilterIdentifiers]
    );

    const normalizedCurrentFilters = useMemo(
        () => normalizeFilters(getDisplayFilters(currentFilters, excludedFilterIdentifiers)),
        [currentFilters, excludedFilterIdentifiers]
    );

    const appliedSavedFilter = useMemo(() => {
        if (!appliedSavedFilterId) return null;
        return savedFilters.find((s) => s.id === appliedSavedFilterId) || null;
    }, [appliedSavedFilterId, savedFilters]);

    const matchingSavedFilter = useMemo(
        () =>
            preferencesLoaded
                ? findMatchingSavedFilter(savedFilters, normalizedCurrentFilters, excludedFilterIdentifiers)
                : null,
        [preferencesLoaded, savedFilters, normalizedCurrentFilters, excludedFilterIdentifiers]
    );

    const hasActiveFilters = useMemo(
        () => getDisplayFilters(currentFilters, excludedFilterIdentifiers).length > 0,
        [currentFilters, excludedFilterIdentifiers]
    );

    const showNewTabCta = pinnedSavedFilters.length === 0;

    const newTabDisabledReason = useMemo((): NewTabDisabledReason | null => {
        if (!hasActiveFilters) return 'noFilter';

        if (matchingSavedFilter?.pinAsTab) return 'alreadyPinned';

        return null;
    }, [hasActiveFilters, matchingSavedFilter]);

    const canCreateNewTab = showNewTabCta && newTabDisabledReason === null;

    const newTabSaveModalConfig = useMemo((): NewTabSaveModalConfig | null => {
        if (!canCreateNewTab) return null;
        if (matchingSavedFilter?.id) {
            return {
                mode: 'edit',
                initialData: {
                    name: matchingSavedFilter.name,
                    description: matchingSavedFilter.description || '',
                    shared: matchingSavedFilter.shared,
                    pinAsTab: true
                },
                preferenceId: matchingSavedFilter.id,
                accountId: matchingSavedFilter.accountId || activeOrganizationAccount
            };
        }

        return {
            mode: 'create',
            initialData: {
                name: '',
                description: '',
                shared: false,
                pinAsTab: true
            }
        };
    }, [canCreateNewTab, matchingSavedFilter, activeOrganizationAccount]);

    // Determine if the current active filters differ from the applied saved filter (ignoring excluded filters).
    // Suppress during tab transitions: other useSavedFilters instances may lag on appliedSavedFilterId
    // while Searchkit filters already match another saved filter or have been cleared to All.
    const hasUnsavedChanges = useMemo(() => {
        if (!appliedSavedFilter || !hasActiveFilters || matchingSavedFilter) return false;
        return !isEqual(
            normalizeFilters(getDisplayFilters(currentFilters, excludedFilterIdentifiers)),
            normalizeFilters(getDisplayFilters(appliedSavedFilter.filters, excludedFilterIdentifiers))
        );
    }, [currentFilters, appliedSavedFilter, excludedFilterIdentifiers, hasActiveFilters, matchingSavedFilter]);

    const applySavedFilter = useCallback(
        (preferenceId: string, filters?: Filter[]) => {
            if (!api) return;
            const { requiredFilters = [], immutableFilters = [] } = options;

            const filtersToApply = filters || savedFilters.find((s) => s.id === preferenceId)?.filters;
            if (!filtersToApply) {
                failedToastMessage('filter.savedFilter.apply.failed');
                return;
            }

            const currentState = api.searchState;
            const baseFilters = composeBaseFilters(currentState, requiredFilters, immutableFilters);
            const preserved = new Set(baseFilters.map(buildFilterKey));
            const uniqueIncoming = filtersToApply.filter((f) => !preserved.has(buildFilterKey(f)));
            const newFilters = [...baseFilters, ...uniqueIncoming];

            const filtersChanged = !isEqual(normalizeFilters(currentFilters), normalizeFilters(newFilters));
            if (filtersChanged) {
                applySearchState(api, currentState, newFilters);
            }
            setAppliedSavedFilterId(preferenceId);
            // Persist to localStorage for refresh restoration
            writeSelectedSavedFilter(storageKey, {
                preferenceId,
                filters: filtersToApply
            });
        },
        [api, options, savedFilters, storageKey, currentFilters]
    );

    const clearAppliedSavedFilter = useCallback(() => {
        if (!api) return;
        const { requiredFilters = [], immutableFilters = [] } = options;
        const currentState = api.searchState;
        const newFilters = composeBaseFilters(currentState, requiredFilters, immutableFilters);
        applySearchState(api, currentState, newFilters);
        setAppliedSavedFilterId(null);
        clearSelectedSavedFilter(storageKey);
    }, [api, options, storageKey]);

    useEffect(() => {
        if (!api || !preferencesLoaded || !savedFilters.length || savingFilter) return;

        if (matchingSavedFilter?.id && appliedSavedFilterId !== matchingSavedFilter.id) {
            applySavedFilter(matchingSavedFilter.id);
        } else if (!matchingSavedFilter && appliedSavedFilterId && !hasActiveFilters) {
            clearAppliedSavedFilter();
        }
    }, [
        api,
        preferencesLoaded,
        savedFilters,
        matchingSavedFilter,
        appliedSavedFilterId,
        applySavedFilter,
        clearAppliedSavedFilter,
        savingFilter,
        hasActiveFilters
    ]);

    // Persist current filters whenever they change while a saved filter is applied
    // This ensures removals (e.g., unchecking filters) are reflected in localStorage.
    // Skip once no user-facing filters remain: the applied filter is being cleared and other
    // instances of this hook still hold the previous id, which would rewrite the entry that
    // clearAppliedSavedFilter just removed.
    useEffect(() => {
        if (!appliedSavedFilterId || savingFilter || !hasActiveFilters) return;
        writeSelectedSavedFilter(storageKey, {
            preferenceId: appliedSavedFilterId,
            filters: currentFilters
        });
    }, [appliedSavedFilterId, storageKey, savingFilter, currentFilters, hasActiveFilters]);

    // Restore saved filter when preferences load and there are no active saved filters applied
    useEffect(() => {
        if (hasRestoredRef.current || !api || !preferencesLoaded || !savedFilters.length || appliedSavedFilterId)
            return;

        const persistedSelectedFilter = readSelectedSavedFilter(storageKey);
        if (!persistedSelectedFilter?.preferenceId) return;

        hasRestoredRef.current = true;

        if (persistedSelectedFilter.filters) {
            // Restoring a snapshot without user-facing filters would replace the Searchkit state with
            // base filters only, discarding the filters restored from the list's own persistence.
            if (getDisplayFilters(persistedSelectedFilter.filters, excludedFilterIdentifiers).length) {
                applySavedFilter(persistedSelectedFilter.preferenceId, persistedSelectedFilter.filters);
            } else {
                clearSelectedSavedFilter(storageKey);
            }
            return;
        }

        const match = savedFilters.find((s) => s.id === persistedSelectedFilter.preferenceId);
        if (match) {
            applySavedFilter(persistedSelectedFilter.preferenceId);
        } else {
            clearSelectedSavedFilter(storageKey);
        }
    }, [
        preferencesLoaded,
        api,
        savedFilters,
        storageKey,
        applySavedFilter,
        excludedFilterIdentifiers,
        appliedSavedFilterId
    ]);

    const saveFilter = useCallback(
        async (formData: SavePreferenceFormData, filters?: Filter[], accountId?: string, preferenceId?: string) => {
            setSavingFilter(true);

            const filtersToUse = filters || currentFilters;
            const displayFilters = getDisplayFilters(filtersToUse, excludedFilterIdentifiers);
            const { filtered, name, description } = processFilterData(displayFilters, formData);
            const existingSavedFilter = preferenceId
                ? savedFilters.find((filter) => filter.id === preferenceId)
                : undefined;
            const pinAsTab = Boolean(formData.pinAsTab);
            const value = serializeSavedFilterPreferenceValue({ filters: filtered });
            const summary = { about: description, headline: '' };

            try {
                const res = await savePreferences({
                    preferenceInput: {
                        ...(preferenceId && { identity: { id: preferenceId } }),
                        ...(accountId && { accountId }),
                        view: '',
                        key: preferenceKey,
                        value,
                        agent: '',
                        name,
                        summary
                    },
                    shareInput: formData.shared
                        ? {
                              sharedWithOrganizationAccounts: [
                                  {
                                      actor: {
                                          id: activeOrganizationAccount,
                                          name: organizationAccountDetails?.organizationName || '',
                                          email: organizationAccountDetails?.adminEmail || ''
                                      },
                                      action: FORMAT_API_ACTION.manage
                                  }
                              ],
                              notify: false
                          }
                        : null,
                    showSuccessMessages: false
                });

                if (!res?.id) return;

                let updatedPreferences = buildUpdatedPreferencesAfterSave({
                    preferences,
                    preferenceId,
                    savedPreferenceId: res.id,
                    value,
                    name,
                    summary,
                    shared: formData?.shared,
                    userAccountId: userProfile.accountId,
                    applicationName: application.name,
                    preferenceKey
                });

                // Seed from legacy pins on first personal write so other tabs are not dropped.
                const basePinnedValue: PinnedSavedFiltersPreferenceValue =
                    personalPinnedTabs?.value ?? seedPinnedTabsFromSavedFilters(savedFilters);

                const nextPinnedValue = upsertPinnedTab(
                    basePinnedValue,
                    res.id,
                    pinAsTab,
                    existingSavedFilter?.pinAsTab ? existingSavedFilter.pinnedAt : undefined
                );

                updatedPreferences = await persistPersonalPinnedTabs(nextPinnedValue, updatedPreferences);
                refreshPreferences(undefined, updatedPreferences, false);
                applySavedFilter(res.id, filtered);
                if (preferenceId) {
                    setNewTabSaveModalOpen(false);
                    successToastMessage('filter.savedFilter.update.success');
                } else {
                    setSaveFilterOpen(false);
                    setNewTabSaveModalOpen(false);
                    successToastMessage('filter.savedFilter.create.success');
                }
            } catch (error) {
                if (preferenceId) {
                    failedToastMessage('filter.savedFilter.update.failed');
                } else {
                    failedToastMessage('filter.savedFilter.create.failed');
                }
            } finally {
                setSavingFilter(false);
            }
        },
        [
            currentFilters,
            excludedFilterIdentifiers,
            savePreferences,
            refreshPreferences,
            applySavedFilter,
            preferences,
            activeOrganizationAccount,
            organizationAccountDetails,
            userProfile,
            preferenceKey,
            application.name,
            savedFilters,
            personalPinnedTabs,
            persistPersonalPinnedTabs
        ]
    );

    const saveAppliedFilterChanges = useCallback(async () => {
        if (!appliedSavedFilter?.id) return;
        await saveFilter(
            {
                name: appliedSavedFilter.name,
                description: appliedSavedFilter.description || '',
                shared: appliedSavedFilter.shared,
                pinAsTab: appliedSavedFilter.pinAsTab
            },
            currentFilters,
            appliedSavedFilter.accountId || activeOrganizationAccount,
            appliedSavedFilter.id
        );
    }, [appliedSavedFilter, currentFilters, saveFilter, activeOrganizationAccount]);

    const openNewTabSaveModal = useCallback(() => {
        if (!canCreateNewTab || !newTabSaveModalConfig) return;
        setSaveFilterOpen(false);
        setNewTabSaveModalOpen(true);
    }, [canCreateNewTab, newTabSaveModalConfig]);

    const closeNewTabSaveModal = useCallback(() => {
        setNewTabSaveModalOpen(false);
    }, []);

    const deleteFilter = useCallback(
        async (id: string) => {
            setDeletingFilter(true);
            try {
                await deletePreference(id);
                let updatedPreferences = preferences.filter((pref) => pref.id !== id);

                if (personalPinnedTabs?.value.pinnedTabs?.some((tab) => tab.preferenceId === id)) {
                    updatedPreferences = await persistPersonalPinnedTabs(
                        upsertPinnedTab(personalPinnedTabs.value, id, false),
                        updatedPreferences
                    );
                }

                refreshPreferences(undefined, updatedPreferences, false);
                if (appliedSavedFilter?.id === id) clearAppliedSavedFilter();
                successToastMessage('filter.savedFilter.delete.success');
            } catch (e) {
                failedToastMessage('filter.savedFilter.delete.failed');
            } finally {
                setDeletingFilter(false);
            }
        },
        [
            deletePreference,
            refreshPreferences,
            appliedSavedFilter,
            clearAppliedSavedFilter,
            preferences,
            personalPinnedTabs,
            persistPersonalPinnedTabs
        ]
    );

    const reorderPinnedSavedFilterTabs = useCallback(
        async (orderedPreferenceIds: string[]) => {
            if (!orderedPreferenceIds.length || persistingPinnedTabsRef.current) return;

            persistingPinnedTabsRef.current = true;

            try {
                const basePinnedValue: PinnedSavedFiltersPreferenceValue =
                    personalPinnedTabs?.value ?? seedPinnedTabsFromSavedFilters(savedFilters);
                const nextPinnedValue = reorderPinnedTabs(basePinnedValue, orderedPreferenceIds);
                const optimisticPreferences = buildOptimisticPinnedTabsPreferences({
                    preferences,
                    existingPinnedPreference: personalPinnedTabs?.preference,
                    nextPinnedValue,
                    accountId: userProfile.accountId,
                    applicationName: application.name,
                    pinnedTabsPreferenceKey
                });

                refreshPreferences(undefined, optimisticPreferences, false);

                const updatedPreferences = await persistPersonalPinnedTabs(nextPinnedValue, optimisticPreferences);
                refreshPreferences(undefined, updatedPreferences, false);
            } catch (error) {
                refreshPreferences(undefined, preferences, false);
                failedToastMessage('filter.savedFilter.update.failed');
            } finally {
                persistingPinnedTabsRef.current = false;
            }
        },
        [
            personalPinnedTabs,
            savedFilters,
            persistPersonalPinnedTabs,
            preferences,
            refreshPreferences,
            userProfile.accountId,
            application.name,
            pinnedTabsPreferenceKey
        ]
    );

    const unpinSavedFilterTab = useCallback(
        async (preferenceId: string) => {
            if (!preferenceId) return;
            if (persistingPinnedTabsRef.current) return;

            persistingPinnedTabsRef.current = true;

            const shouldClearApplied = appliedSavedFilterId === preferenceId;

            try {
                const basePinnedValue: PinnedSavedFiltersPreferenceValue =
                    personalPinnedTabs?.value ?? seedPinnedTabsFromSavedFilters(savedFilters);

                if (!basePinnedValue.pinnedTabs.some((tab) => tab.preferenceId === preferenceId)) {
                    return;
                }

                const nextPinnedValue = upsertPinnedTab(basePinnedValue, preferenceId, false);
                const optimisticPreferences = buildOptimisticPinnedTabsPreferences({
                    preferences,
                    existingPinnedPreference: personalPinnedTabs?.preference,
                    nextPinnedValue,
                    accountId: userProfile.accountId,
                    applicationName: application.name,
                    pinnedTabsPreferenceKey
                });

                refreshPreferences(undefined, optimisticPreferences, false);
                if (shouldClearApplied) {
                    clearAppliedSavedFilter();
                }

                const updatedPreferences = await persistPersonalPinnedTabs(nextPinnedValue, optimisticPreferences);
                refreshPreferences(undefined, updatedPreferences, false);
                successToastMessage('filter.savedFilter.unpinTab.success');
            } catch (error) {
                refreshPreferences(undefined, preferences, false);
                if (shouldClearApplied) {
                    applySavedFilter(preferenceId);
                }
                failedToastMessage('filter.savedFilter.update.failed');
            } finally {
                persistingPinnedTabsRef.current = false;
            }
        },
        [
            personalPinnedTabs,
            savedFilters,
            persistPersonalPinnedTabs,
            preferences,
            refreshPreferences,
            appliedSavedFilterId,
            clearAppliedSavedFilter,
            applySavedFilter,
            userProfile.accountId,
            application.name,
            pinnedTabsPreferenceKey
        ]
    );

    return {
        state: {
            saveFilterOpen,
            appliedSavedFilter,
            hasActiveFilters,
            hasUnsavedChanges,
            savingFilter,
            deletingFilter,
            savedFilters,
            pinnedSavedFilters,
            currentFilters,
            showNewTabCta,
            newTabDisabledReason,
            canCreateNewTab,
            newTabSaveModalConfig,
            newTabSaveModalOpen
        },
        actions: {
            setSaveFilterOpen,
            saveFilter,
            saveAppliedFilterChanges,
            deleteFilter,
            applySavedFilter,
            clearAppliedSavedFilter,
            setAppliedSavedFilterId,
            openNewTabSaveModal,
            closeNewTabSaveModal,
            reorderPinnedTabs: reorderPinnedSavedFilterTabs,
            unpinSavedFilterTab
        }
    };
};
